<template>
  <div ref="tableContainer">
    <el-row class="page-buttons">
      <editor-button v-if="hasCreatePermission" action="add" @click="handleCreate" />
      <editor-button v-if="filtersActive" action="clearFilters" @click="clearAllFilters" />
    </el-row>
    <pagination
      v-if="numberOfItems > 0"
      :total="numberOfItems"
      :page.sync="listQuery.page"
      :limit.sync="listQuery.limit"
      @pagination="paginateList"
    />
    <el-table
      ref="crudTable"
      v-loading="loading"
      :data="paginatedItems"
      border
      highlight-current-row
      :max-height="getMaxTableHeight()"
      @row-dblclick="handleEdit"
    >
      <el-table-column label-class-name="filter-cell">
        <template slot="header" slot-scope="{}">
          <el-input
            v-model="search['productCode']"
            class="search"
            size="mini"
            :placeholder="$t('priceListItem.productCode')"
            clearable
            prefix-icon="el-icon-search"
          />
        </template>
        <el-table-column :label="$t('priceListItem.productCode')" property="code" width="90">
          <template slot-scope="scope">
            <span>{{ scope.row.productCode }}</span>
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column label-class-name="filter-cell">
        <template slot="header" slot-scope="{}">
          <el-input
            v-model="search['description']"
            class="search"
            size="mini"
            :placeholder="$t('common.description')"
            clearable
            prefix-icon="el-icon-search"
          />
        </template>
        <el-table-column :label="$t('common.description')" property="description" width="420">
          <template slot-scope="scope">
            <span>{{ scope.row.description }}</span>
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column label-class-name="filter-cell">
        <template slot="header" slot-scope="{}">
          <el-input
            v-model="search['descriptionInvoice']"
            class="search"
            size="mini"
            :placeholder="$t('priceListItem.descriptionInvoice')"
            clearable
            prefix-icon="el-icon-search"
          />
        </template>
        <el-table-column :label="$t('priceListItem.descriptionInvoice')" property="descriptionInvoice" width="420">
          <template slot-scope="scope">
            <span>{{ scope.row.descriptionInvoice }}</span>
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column label-class-name="filter-cell">
        <template slot="header" slot-scope="{}">
          <el-select
            v-model="search['unitOption']"
            class="search"
            size="mini"
            :placeholder="$t('priceListItem.unit')"
            clearable
            :options="[]"
            prefix-icon="el-icon-search"
          />
        </template>
        <el-table-column :label="$t('priceListItem.unit')" property="description" width="100">
          <template slot-scope="scope">
            <span>{{ formatUnitOption(scope.row.unitOption) }}</span>
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column :label="$t('priceListItem.markUpYN')" width="75">
        <template slot-scope="scope">
          <span>{{ scope.row.markUpYN ? $t('common.yes') : $t('common.no') }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('priceListItem.matrixYN')" width="75">
        <template slot-scope="scope">
          <span>{{ scope.row.matrixYN ? $t('common.yes') : $t('common.no') }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('priceListItem.laborPrice')" property="stockTotal" width="75">
        <template slot-scope="scope">
          <span>{{ getLaborPrice(scope.row) }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('priceListItemPrice.consumerPrices')">
        <el-table-column v-for="culture in culturesForDomain" :key="culture.id" :label="culture.description">
          <template slot-scope="scope">
            <span>{{ getRowAmountForCulture(scope.row, culture.cultureID) }}</span>
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column :label="$t('priceListItemPrice.workshopPrices')">
        <el-table-column v-for="country in countriesUsedInWorkshops" :key="country.id" :label="country.name">
          <template slot-scope="scope">
            <span>{{ getRowAmountForCountry(scope.row, country.countryID) }}</span>
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column :label="$t('priceListItem.intercompany')" width="45">
        <template slot-scope="scope">
          <span>{{ scope.row.externalOnlyYN ? $t('common.no') : $t('common.yes') }}</span>
        </template>
      </el-table-column>
      <el-table-column label-class-name="filter-cell">
        <template slot="header" slot-scope="{}">
          <el-input
            v-model="search['descriptionInvoiceForeign']"
            class="search"
            size="mini"
            :placeholder="$t('priceListItem.descriptionInvoiceForeign')"
            clearable
            prefix-icon="el-icon-search"
          />
        </template>
        <el-table-column
          :label="$t('priceListItem.descriptionInvoiceForeign')"
          property="descriptionInvoiceForeign"
          width="420"
        >
          <template slot-scope="scope">
            <span>{{ scope.row.descriptionInvoiceForeign }}</span>
          </template>
        </el-table-column>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import EditorButton from '@/components/crud/EditorButton';
import Pagination from '@/components/Pagination';
import formMixin from './form-mixin';
import CrudTablePersistentState from '@/components/crud/CrudTablePersistentState';
import { hasCreatePermissionForCode, hasEditPermissionForCode } from '@/utils/permission';

const persistentState = new CrudTablePersistentState();

export default {
  components: { EditorButton, Pagination },
  mixins: [formMixin],
  props: {
    caption: String,
    parentIdFilterField: {
      type: String,
      required: true
    },
    permissionCode: String,
    disabled: {
      default: false,
      type: Boolean
    },
    openInFullscreen: {
      default: false,
      type: Boolean
    },
    required: {
      default: false,
      type: Boolean
    }
  },
  data() {
    return {
      items: this.$store.getters['priceListItems/loadedItems'],
      filteredItems: [],
      paginatedItems: [],
      numberOfItems: 0,
      culturesForDomain: [],
      countriesUsedInWorkshops: [],
      loading: false,
      defaultCellAlign: 'left',
      listQuery: {
        limit: 50,
        page: 1
      },
      search: persistentState.getDefaultSearch(this.filters)
    };
  },
  computed: {
    hasCreatePermission() {
      return hasCreatePermissionForCode(this.permissionCode || this.entity, this.$store.getters.permissions);
    },
    hasDeletePermission() {
      return this.hasCreatePermission;
    },
    hasEditPermission() {
      return hasEditPermissionForCode(this.permissionCode || this.entity, this.$store.getters.permissions);
    },
    filtersActive() {
      return Object.keys(this.search).some(k => this.search[k]);
    }
  },
  watch: {
    search: {
      deep: true,
      handler: function(value) {
        this.listQuery.page = 1;
        this.filterAllItems();
        this.numberOfItems = this.filteredItems.length;
        this.paginateList({ limit: this.listQuery.limit, page: 1 });
      }
    }
  },
  created() {
    this.loading = true;
    this.getCultureColumns();
    this.getCountryColumns();
    const params = { query: {} };
    params.query[this.parentIdFilterField] = +this.$route.params.id;
    this.$store
      .dispatch('priceListItem/getItems', params)
      .then(() => {
        this.numberOfItems = this.$store.getters['priceListItem/totalItemsCount'];
        this.items = this.$store.getters['priceListItem/loadedItems'];
        this.filterAllItems();
        this.paginateList({ limit: this.listQuery.limit, page: 1 });
      })
      .finally(() => {
        this.loading = false;
      });
  },
  methods: {
    getCultureColumns() {
      this.$store.dispatch('domain/getCulturesForDomain', +this.$route.params.id).then(data => {
        this.culturesForDomain = data;
      });
    },
    getCountryColumns() {
      this.$store.dispatch('workshop/getCountriesUsedInWorkshops').then(data => {
        this.countriesUsedInWorkshops = data;
      });
    },
    getRowAmountForCulture(row, id) {
      if (row.matrixYN) {
        return '';
      }
      const culture = row.culturePrices.find(cp => cp.id === id);
      if (culture && culture.price !== undefined && culture.price !== null) {
        return (culture.symbol || '') + ' ' + culture.price.toFixed(2);
      } else {
        return '';
      }
    },
    getRowAmountForCountry(row, id) {
      if (row.matrixYN) {
        return '';
      }
      const country = row.countryPrices.find(cp => cp.id === id);
      if (country && country.price !== undefined && country.price !== null) {
        return (country.symbol || '') + ' ' + country.price.toFixed(2);
      } else {
        return '';
      }
    },
    getLaborPrice(row) {
      if (row.matrixYN) {
        return '';
      } else if (row.laborPrice === undefined || row.laborPrice === null) {
        return '';
      }
      return row.laborSymbol + ' ' + row.laborPrice.toFixed(2);
    },
    formatUnitOption(unitOption) {
      const option = this.$store.getters['option/loadedOptions']('capacityUnit').find(item => item.code === unitOption);
      return option && option.description;
    },
    filterAllItems() {
      this.filteredItems = [...this.items];
      if (this.search.productCode && this.search.productCode.length) {
        this.filteredItems = this.filteredItems.filter(
          e => e.productCode.toLowerCase().indexOf(this.search.productCode.toLowerCase()) !== -1
        );
      }
      if (this.search.description && this.search.description.length) {
        this.filteredItems = this.filteredItems.filter(
          e => e.description.toLowerCase().indexOf(this.search.description.toLowerCase()) !== -1
        );
      }
      if (this.search.descriptionInvoice && this.search.descriptionInvoice.length) {
        this.filteredItems = this.filteredItems.filter(
          e => e.descriptionInvoice.toLowerCase().indexOf(this.search.descriptionInvoice.toLowerCase()) !== -1
        );
      }
    },
    generateSummary(row) {
      return `${row.description}`;
    },
    handleCreate() {
      const location = {
        name: 'Create' + this.entity,
        params: {
          domainID: +this.$route.params.id
        }
      };
      this.$router.push(location);
      this.$emit('create');
    },
    handleEdit(row) {
      const location = {
        name: 'Edit' + this.entity,
        params: {
          domainID: +this.$route.params.id,
          id: row.id
        },
        query: {
          matrixYN: row.matrixYN
        }
      };
      this.$router.push(location);
      this.$emit('edit', row.id);
    },
    paginateList(pageInfo) {
      const startIndex = (this.listQuery.page - 1) * this.listQuery.limit;
      const endIndex = startIndex + this.listQuery.limit;
      this.paginatedItems = this.filteredItems.slice(startIndex, endIndex);
      this.listQuery.page = pageInfo.page;
    },
    clearAllFilters() {
      this.$refs.crudTable.clearFilter();
      this.search = {};
    },
    getMaxTableHeight() {
      if (this.$refs.tableContainer) {
        const spaceForButtons = 150;
        return window.innerHeight - this.$refs.tableContainer.getBoundingClientRect().top - spaceForButtons;
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
